import React, { useEffect, useState } from 'react';
import {
  TRAINING_CYCLE_LABELS,
  type SubscriptionPlan,
  type TrainingCycle,
} from '../../../types';
import FormInput from '../../../components/common/FormInput/FormInput';
import FormSelect from '../../../components/common/FormSelect/FormSelect';
import FormTextarea from '../../../components/common/FormTextarea/FormTextarea';
import Checkbox from '../../../components/common/Checkbox/Checkbox';
import { formatCurrencyInput, parseCurrencyInput } from '../../../utils';
import { expectedLoyaltyMonthsForBillingCycle } from '../../../utils/subscriptionLifecycle';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { useMarketingProjects } from '../../../hooks/useMarketingProjects';
import styles from './SubscriptionPlanModal.module.css';

const BILLING_CYCLE_OPTIONS = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'bimonthly', label: 'Bimestral' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'yearly', label: 'Anual' },
] as const;

const TRAINING_CYCLE_OPTIONS = (
  Object.entries(TRAINING_CYCLE_LABELS) as Array<[TrainingCycle, string]>
).map(([value, label]) => ({ value, label }));

const isTrainingCycle = (value: string): value is TrainingCycle => (
  Object.prototype.hasOwnProperty.call(TRAINING_CYCLE_LABELS, value)
);

interface FormState {
  name: string;
  priceInput: string;
  billingCycle: string;
  trainingCycle: TrainingCycle;
  loyalty_months: string;
  autoRenew: boolean;
  isSinglePayment: boolean;
  description: string;
  benefits: string;
  is_active: boolean;
  isVisibleInApp: boolean;
  projectId: string;
}

interface FormErrors {
  name?: string;
  priceInput?: string;
  loyalty_months?: string;
}

interface SubscriptionPlanModalProps {
  open: boolean;
  plan: SubscriptionPlan | null;
  loading: boolean;
  canManage?: boolean;
  onConfirm: (payload: Partial<SubscriptionPlan>) => Promise<void>;
  onDelete: (plan: SubscriptionPlan) => Promise<void>;
  onCancel: () => void;
}

const emptyForm = (): FormState => ({
  name: '',
  priceInput: '',
  billingCycle: 'monthly',
  trainingCycle: 'monthly',
  loyalty_months: '0',
  autoRenew: true,
  isSinglePayment: false,
  description: '',
  benefits: '',
  is_active: true,
  isVisibleInApp: true,
  projectId: '',
});

const planToForm = (plan: SubscriptionPlan): FormState => ({
  name: plan.name,
  priceInput: formatCurrencyInput(plan.price_cents / 100),
  billingCycle: plan.billing_cycle || 'monthly',
  trainingCycle: isTrainingCycle(plan.training_cycle) ? plan.training_cycle : 'monthly',
  loyalty_months: String(plan.loyalty_months),
  autoRenew: plan.auto_renew,
  isSinglePayment: Boolean(plan.is_single_payment),
  description: plan.description ?? '',
  benefits: plan.benefits ?? '',
  is_active: plan.is_active,
  isVisibleInApp: plan.is_visible_in_app !== false,
  projectId: plan.project_id ? String(plan.project_id) : '',
});

const parseSelectedProjectId = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const validate = (form: FormState): FormErrors => {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = 'Nome é obrigatório.';
  const price = parseCurrencyInput(form.priceInput);
  if (!form.priceInput.trim() || price === null || price < 0) {
    errors.priceInput = 'Informe um valor válido.';
  }

  const expectedLoyalty = expectedLoyaltyMonthsForBillingCycle(form.billingCycle);
  if (expectedLoyalty !== null) {
    const loyalty = parseInt(form.loyalty_months, 10);
    if (!Number.isInteger(loyalty) || loyalty !== expectedLoyalty) {
      errors.loyalty_months = `Para este ciclo, a fidelidade deve ser ${expectedLoyalty} meses.`;
    }
  }

  return errors;
};

const SubscriptionPlanModal: React.FC<SubscriptionPlanModalProps> = ({
  open,
  plan,
  loading,
  canManage = true,
  onConfirm,
  onDelete,
  onCancel,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const projects = useMarketingProjects(open);

  useEffect(() => {
    if (!open) return;
    const next = plan ? planToForm(plan) : emptyForm();
    const expectedLoyalty = expectedLoyaltyMonthsForBillingCycle(next.billingCycle);
    if (expectedLoyalty !== null) {
      next.loyalty_months = String(expectedLoyalty);
    }
    setForm(next);
    setErrors({});
    setSaveError(null);
  }, [open, plan]);

  useEscapeToClose(onCancel, open && !loading);

  if (!open) return null;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => {
      if (key === 'billingCycle' && typeof value === 'string') {
        const expectedLoyalty = expectedLoyaltyMonthsForBillingCycle(value);
        return {
          ...prev,
          billingCycle: value,
          isSinglePayment: value.trim().toLowerCase() !== 'monthly' ? true : prev.isSinglePayment,
          ...(expectedLoyalty !== null ? { loyalty_months: String(expectedLoyalty) } : {}),
        };
      }

      return { ...prev, [key]: value };
    });
    setErrors((prev) => ({ ...prev, [key]: undefined, loyalty_months: undefined }));
  };

  const handleSubmit = async () => {
    const expectedLoyalty = expectedLoyaltyMonthsForBillingCycle(form.billingCycle);
    const formForValidation = expectedLoyalty !== null
      ? { ...form, loyalty_months: String(expectedLoyalty) }
      : form;
    if (expectedLoyalty !== null && form.loyalty_months !== String(expectedLoyalty)) {
      setForm(formForValidation);
    }

    const validationErrors = validate(formForValidation);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const price = parseCurrencyInput(formForValidation.priceInput) ?? 0;
    const normalizedBillingCycle = formForValidation.billingCycle.trim().toLowerCase();
    const isSinglePayment = formForValidation.isSinglePayment || normalizedBillingCycle !== 'monthly';
    const payload: Partial<SubscriptionPlan> = {
      name: formForValidation.name.trim(),
      price_cents: Math.round(price * 100),
      billing_cycle: formForValidation.billingCycle,
      training_cycle: formForValidation.trainingCycle,
      loyalty_months: expectedLoyalty
        ?? (parseInt(formForValidation.loyalty_months, 10) || 0),
      auto_renew: isSinglePayment ? false : formForValidation.autoRenew,
      is_single_payment: isSinglePayment,
      description: formForValidation.description.trim() || null,
      benefits: formForValidation.benefits.trim() || null,
      is_active: formForValidation.is_active,
      is_visible_in_app: formForValidation.isVisibleInApp,
      project_id: parseSelectedProjectId(formForValidation.projectId),
    };

    setSaveError(null);
    try {
      await onConfirm(payload);
    } catch {
      setSaveError('Erro ao salvar. Tente novamente.');
    }
  };

  const title = plan ? 'Editar plano' : 'Novo plano';
  const isSinglePaymentEffective = form.isSinglePayment || form.billingCycle.trim().toLowerCase() !== 'monthly';
  const expectedLoyaltyMonths = expectedLoyaltyMonthsForBillingCycle(form.billingCycle);
  const isLoyaltyLockedToBillingCycle = expectedLoyaltyMonths !== null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles['icon-circle']}>
          <span className="material-symbols-outlined">subscriptions</span>
        </div>

        <h3 className={styles.title}>{title}</h3>

        <div className={styles['form-grid']}>
          <FormInput
            id="plan-name"
            label="Nome"
            required
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            error={errors.name}
            disabled={loading}
            placeholder="Ex: Plano Mensal"
          />

          <FormSelect
            id="plan-project"
            label="Projeto"
            options={projects.map((project) => ({
              value: String(project.id),
              label: `${project.code} — ${project.name}`,
            }))}
            value={form.projectId}
            onChange={(e) => setField('projectId', e.target.value)}
            disabled={loading}
            placeholder="Sem projeto"
            allowEmpty
          />

          <FormInput
            id="plan-price"
            label="Preço (R$)"
            required
            value={form.priceInput}
            onChange={(e) => setField('priceInput', formatCurrencyInput(e.target.value))}
            error={errors.priceInput}
            disabled={loading}
            placeholder="Ex: 99,90"
            inputMode="numeric"
          />

          <FormInput
            id="plan-loyalty-months"
            label="Fidelidade (meses)"
            type="number"
            min={0}
            value={form.loyalty_months}
            onChange={(e) => setField('loyalty_months', e.target.value)}
            error={errors.loyalty_months}
            disabled={loading || isLoyaltyLockedToBillingCycle}
          />

          <FormSelect
            id="plan-billing-cycle"
            label="Ciclo de cobrança"
            options={BILLING_CYCLE_OPTIONS}
            value={form.billingCycle}
            onChange={(e) => setField('billingCycle', e.target.value)}
            disabled={loading}
          />

          <FormSelect
            id="plan-training-cycle"
            label="Ciclo de treino"
            options={TRAINING_CYCLE_OPTIONS}
            value={form.trainingCycle}
            onChange={(e) => {
              const next = e.target.value;
              if (isTrainingCycle(next)) {
                setField('trainingCycle', next);
              }
            }}
            disabled={loading}
          />

          <div className={`${styles['toggle-row']} ${styles['full-col']}`}>
            <Checkbox
              label="À Vista"
              checked={isSinglePaymentEffective}
              onPress={() => setField('isSinglePayment', !form.isSinglePayment)}
              disabled={loading || form.billingCycle.trim().toLowerCase() !== 'monthly'}
              labelPosition="left"
              className={styles['toggle-checkbox']}
              ariaLabel="À Vista"
            />
            {form.billingCycle.trim().toLowerCase() !== 'monthly' && (
              <p className={styles['toggle-hint']}>
                Ciclos diferentes de mensal são tratados como pagamento à vista.
              </p>
            )}
          </div>

          <FormTextarea
            id="plan-description"
            label="Descrição"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            disabled={loading}
            rows={2}
            containerClassName={styles['full-col']}
          />

          <FormTextarea
            id="plan-benefits"
            label="Benefícios"
            value={form.benefits}
            onChange={(e) => setField('benefits', e.target.value)}
            disabled={loading}
            rows={2}
            containerClassName={styles['full-col']}
          />

          <div className={`${styles['toggles-row']} ${styles['full-col']}`}>
            <div className={styles['toggle-row']}>
              <Checkbox
                label="Renovação automática"
                checked={form.autoRenew && !isSinglePaymentEffective}
                onPress={() => setField('autoRenew', !form.autoRenew)}
                disabled={loading || isSinglePaymentEffective}
                labelPosition="left"
                className={styles['toggle-checkbox']}
                ariaLabel="Renovação automática"
              />
            </div>

            <div className={styles['toggle-row']}>
              <Checkbox
                label="Plano ativo"
                checked={form.is_active}
                onPress={() => setField('is_active', !form.is_active)}
                disabled={loading}
                labelPosition="left"
                className={styles['toggle-checkbox']}
                ariaLabel="Plano ativo"
              />
            </div>

            <div className={styles['toggle-row']}>
              <Checkbox
                label="Aparecer no app"
                checked={form.isVisibleInApp}
                onPress={() => setField('isVisibleInApp', !form.isVisibleInApp)}
                disabled={loading}
                labelPosition="left"
                className={styles['toggle-checkbox']}
                ariaLabel="Aparecer no app"
              />
            </div>
          </div>
        </div>

        {saveError && <p className={styles['save-error']}>{saveError}</p>}

        <div className={styles.actions}>
          {plan && canManage && (
            <button
              className={`${styles.btn} ${styles['btn-delete']}`}
              onClick={() => onDelete(plan)}
              disabled={loading}
              type="button"
            >
              Excluir
            </button>
          )}
          <button
            className={`${styles.btn} ${styles['btn-cancel']}`}
            onClick={onCancel}
            disabled={loading}
            type="button"
          >
            Cancelar
          </button>
          <button
            className={`${styles.btn} ${styles['btn-confirm']}`}
            onClick={handleSubmit}
            disabled={loading || !canManage}
            type="button"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlanModal;
